// ─────────────────────────────────────────────────────────────────────────────
// tap_ring.ts — Self-managing tap ring interactors
//
// Each tap leaves a TapRing on the canvas — a visible, interactive arc-
// countdown circle.  Rings own their timing, rendering, hit-testing, and
// lifecycle.  A ring either resolves (pattern fires) or expires and spawns
// a successor ring for the next timing window.
//
// The TapRingLayer is the single interface for the host (canvas_app):
//   • feedTap(tap)       — on pointer-up (confirmed tap)
//   • checkPointerDown() — on pointer-down (terminal ring fires immediately)
//   • draw()             — in the RAF loop
//   • clear()            — on view switch / stroke commit
//
// All pattern state, cadence logic, and visual feedback is internal.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  TapPattern, RhythmGate, RecordedTap,
} from '../../src/core/tap_cadence';

// ── Ring phases ──────────────────────────────────────────────────────────────

type RingPhase = 'expanding' | 'active' | 'fading' | 'dead';

// ── Alive pattern entry — tracks per-pattern state within a ring chain ───────

interface AliveEntry {
  pattern: TapPattern;
  /** How many taps have been accepted so far (including the one that created
   *  the current ring). */
  depth: number;
}

// ── Shadow trail ─────────────────────────────────────────────────────────────

interface Shadow {
  cx: number;
  cy: number;
  color: string;
  radius: number;
  birthMs: number;
  durationMs: number;
}

// ── Impact ring (first-tap visual feedback) ──────────────────────────────────

interface ImpactRing {
  cx: number; cy: number;
  color: string;
  birthMs: number;
}

// ── Spark particle (resolve burst) ───────────────────────────────────────────

interface Spark {
  x: number; y: number;
  vx: number; vy: number;
  life: number;           // 1 → 0
  decay: number;
  color: string;
  size: number;
}

// ── Constants ────────────────────────────────────────────────────────────────

const EXPAND_MS      = 80;      // ghostly expansion phase (shortened)
const FADE_MS        = 280;     // fade-out after expiry / resolve (shortened)
const SHADOW_MS      = 1200;    // shadow trail fade (shortened)
const TARGET_RADIUS  = 20;      // ring radius after expansion
const PROPAGATE_RADIUS = 14;    // propagation wave radius (< ring radius)
const HIT_TOLERANCE  = 8;       // extra px beyond radius for hit-testing
const GHOST_COLOR    = '#b8b0a0';
const NEUTRAL_COLOR  = '#c4c0b8'; // muted neutral for non-invocation rings

const IMPACT_RING_MS = 200;
const IMPACT_RADIUS  = 16;
const TWO_PI         = Math.PI * 2;
const ARC_START      = -Math.PI / 2; // 12 o'clock

// ── Ring successor spec (precomputed before needed) ──────────────────────────

interface RingSpec {
  color: string;
  windowMs: number;           // countdown duration
  isTerminal: boolean;
  resolvePatternId: string | null;
  alive: AliveEntry[];
  taps: RecordedTap[];
}

// ── TapRing ──────────────────────────────────────────────────────────────────

class TapRing {
  cx: number;
  cy: number;
  phase: RingPhase = 'expanding';
  resolved = false;              // true when tapped/resolved (not timed out)
  isOneShot = false;             // true for standalone continuation rings

  // Visual
  color: string;
  targetRadius = TARGET_RADIUS;
  drawRadius = TARGET_RADIUS;   // visual radius (may differ from hit-test radius)
  private _birthMs: number;
  private _expandEnd: number;

  // Countdown window (absolute ms)
  private _windowStart: number;
  private _windowEnd: number;
  windowMs: number;

  // Pattern context
  isTerminal: boolean;
  resolvePatternId: string | null;
  alive: AliveEntry[];
  taps: RecordedTap[];         // accumulated taps so far (shared array ref)

  // Successor — deployed on timeout
  successor: RingSpec | null;

  constructor(
    cx: number, cy: number, now: number,
    color: string, windowMs: number,
    isTerminal: boolean, resolvePatternId: string | null,
    alive: AliveEntry[], taps: RecordedTap[],
    successor: RingSpec | null,
  ) {
    this.cx = cx;
    this.cy = cy;
    this.color = color;
    this.windowMs = windowMs;
    this.isTerminal = isTerminal;
    this.resolvePatternId = resolvePatternId;
    this.alive = alive;
    this.taps = taps;
    this.successor = successor;
    this._birthMs = now;
    this._expandEnd = now + EXPAND_MS;
    // Active window starts after expansion (or at minMs, whichever is later)
    this._windowStart = now + EXPAND_MS;
    this._windowEnd = this._windowStart + windowMs;
  }

  /** Normalised elapsed time within the active window (0 → 1). */
  elapsed(now: number): number {
    if (now < this._windowStart) return 0;
    if (now >= this._windowEnd) return 1;
    return (now - this._windowStart) / (this._windowEnd - this._windowStart);
  }

  /** Advance phase.  Returns true if the ring transitioned to 'fading' or 'dead'. */
  tick(now: number): boolean {
    if (this.phase === 'dead') return false;

    if (this.phase === 'expanding') {
      if (now >= this._expandEnd) {
        this.phase = 'active';
      }
      return false;
    }

    if (this.phase === 'active') {
      if (now >= this._windowEnd) {
        this.phase = 'fading';
        this._birthMs = now; // reuse for fade timing
        return true;         // caller should check successor
      }
      return false;
    }

    // fading
    if (now - this._birthMs > FADE_MS) {
      this.phase = 'dead';
    }
    return false;
  }

  /** Expansion progress 0→1. */
  expandT(now: number): number {
    return Math.min(1, (now - (this._expandEnd - EXPAND_MS)) / EXPAND_MS);
  }

  /** Fade progress 0→1 (only valid in fading phase). */
  fadeT(now: number): number {
    return Math.min(1, (now - this._birthMs) / FADE_MS);
  }

  /** Hit test — true if (x,y) is within the ring's interactive zone. */
  hitTest(x: number, y: number): boolean {
    const dx = x - this.cx, dy = y - this.cy;
    return Math.sqrt(dx * dx + dy * dy) <= this.targetRadius + HIT_TOLERANCE;
  }

  /** Force the ring into fading phase (e.g. when it's been resolved). */
  startFading(now: number): void {
    this.phase = 'fading';
    this._birthMs = now;
  }
}

// ── Easing ───────────────────────────────────────────────────────────────────

function easeOutCubic(t: number): number { return 1 - (1 - t) ** 3; }

// ── Ring schedule computation ────────────────────────────────────────────────

/** Return the rhythm gate for the given inter-tap gap index. */
function gateFor(pat: TapPattern, gapIndex: number): RhythmGate {
  if (pat.rhythm.length === 0) return { minMs: 0, maxMs: Infinity };
  return pat.rhythm[Math.min(gapIndex, pat.rhythm.length - 1)];
}

// ── TapRingLayer ─────────────────────────────────────────────────────────────

export class TapRingLayer {
  private _patterns: TapPattern[] = [];
  private _rings: TapRing[] = [];
  private _shadows: Shadow[] = [];
  private _sparks: Spark[] = [];
  private _impacts: ImpactRing[] = [];

  // Audio callbacks — set by canvas_app
  onTapAccepted: ((tapIndex: number, total: number) => void) | null = null;
  /** Full resolve callback — audio + side effects (wand mode / stylus menu).
   *  Only fired from feedTap() fallback path when checkPointerDown missed. */
  onPatternResolved: ((patternId: string) => void) | null = null;
  /** Audio-only resolve (used by checkPointerDown where caller handles side effects). */
  onResolveAudio: ((patternId: string) => void) | null = null;

  /** Pending one-shot ring awaiting pointer-up confirmation. */
  private _pendingOneShot: TapRing | null = null;

  // ── Registration ───────────────────────────────────────────────────────

  register(pattern: TapPattern): void {
    this._patterns.push(pattern);
    this._patterns.sort((a, b) => b.priority - a.priority);
  }

  // ── Pointer-down probe (one-shot rings store pending, awaiting pointer-up) ─

  checkPointerDown(x: number, y: number, _time: number): {
    patternId: string;
    taps: RecordedTap[];
  } | null {
    for (let i = this._rings.length - 1; i >= 0; i--) {
      const ring = this._rings[i];
      if (ring.phase === 'fading' || ring.phase === 'dead') continue;
      if (!ring.isTerminal) continue;
      if (!ring.isOneShot) continue;
      if (!ring.hitTest(x, y)) continue;

      // Mark ring as pending — will resolve on pointer-up tap confirmation
      this._pendingOneShot = ring;
      return { patternId: ring.resolvePatternId!, taps: [...ring.taps, { x, y, time: _time, strokeId: '' }] };
    }
    return null;
  }

  /**
   * Confirm the pending one-shot ring on pointer-up (tap action).
   * Returns the patternId if the tap landed on the pending ring, else null.
   */
  confirmOneShotTap(x: number, y: number): string | null {
    const ring = this._pendingOneShot;
    this._pendingOneShot = null;
    if (!ring) return null;
    if (ring.phase === 'fading' || ring.phase === 'dead') return null;
    if (!ring.hitTest(x, y)) return null;

    // Resolve!
    const patternId = ring.resolvePatternId!;
    ring.resolved = true;
    this._fadeAllRings(performance.now());
    this._spawnResolveBurst(ring.cx, ring.cy, ring.color);
    this.onResolveAudio?.(patternId);
    return patternId;
  }

  /** Cancel the pending one-shot (e.g. gesture turned into a stroke). */
  cancelPendingOneShot(): void {
    this._pendingOneShot = null;
  }

  /** Hit-test all live one-shot rings at (x, y) and resolve immediately
   *  if one is touched. Used by pen-down so the user gets feedback on
   *  contact rather than having to keep the touch a tap and wait for
   *  pen-up. Returns the patternId on hit, else null. The pending
   *  slot is cleared so pen-up's confirmOneShotTap is a no-op. */
  resolveOneShotAtPointerDown(x: number, y: number): string | null {
    for (let i = this._rings.length - 1; i >= 0; i--) {
      const ring = this._rings[i];
      if (ring.phase === 'fading' || ring.phase === 'dead') continue;
      if (!ring.isTerminal) continue;
      if (!ring.isOneShot) continue;
      if (!ring.hitTest(x, y)) continue;

      ring.resolved = true;
      const patternId = ring.resolvePatternId!;
      this._pendingOneShot = null;
      this._fadeAllRings(performance.now());
      this._spawnResolveBurst(ring.cx, ring.cy, ring.color);
      this.onResolveAudio?.(patternId);
      return patternId;
    }
    return null;
  }

  // ── Feed a confirmed tap ───────────────────────────────────────────────

  feedTap(tap: RecordedTap): 'accepted' | 'new-root' {
    const now = performance.now();

    // Check if any active non-terminal ring catches this tap
    for (let i = this._rings.length - 1; i >= 0; i--) {
      const ring = this._rings[i];
      if (ring.phase !== 'active' && ring.phase !== 'expanding') continue;
      if (!ring.hitTest(tap.x, tap.y)) continue;

      // Ring accepts the tap.  Check if it matches or continues.
      if (ring.isTerminal) {
        // Terminal ring resolved by pointer-up (checkPenDown usually catches
        // this, but handle fallback).  Resolve immediately.
        const patternId = ring.resolvePatternId!;
        const taps = [...ring.taps, tap];
        ring.resolved = true;
        const mx = taps.reduce((s, t) => s + t.x, 0) / taps.length;
        const my = taps.reduce((s, t) => s + t.y, 0) / taps.length;
        this._fadeAllRings(now);
        this._spawnResolveBurst(mx, my, ring.color);
        this.onPatternResolved?.(patternId);
        return 'accepted';
      }

      // Non-terminal continuation: build new alive set with advanced depth.
      const newTaps = [...ring.taps, tap];
      const newAlive: AliveEntry[] = [];

      for (const entry of ring.alive) {
        // Check spatial spread
        const anchor = newTaps[0];
        const dist = Math.sqrt((tap.x - anchor.x) ** 2 + (tap.y - anchor.y) ** 2);
        if (dist > entry.pattern.spreadPx) continue;

        // Check rhythm gate
        const prevTime = newTaps[newTaps.length - 2].time;
        const gap = tap.time - prevTime;
        const gapIdx = entry.depth - 1;
        const gate = gateFor(entry.pattern, gapIdx);
        if (gap < gate.minMs || gap > gate.maxMs) continue;

        newAlive.push({ pattern: entry.pattern, depth: entry.depth + 1 });
      }

      // Fade the caught ring (+ all others)
      this._fadeAllRings(now);

      if (newAlive.length === 0) {
        // All patterns rejected — start fresh root
        this._spawnRootRing(tap, now);
        const total = this._patterns.reduce((m, p) => Math.max(m, p.taps), 0);
        this.onTapAccepted?.(0, total);
        return 'new-root';
      }

      // Check for immediate completion
      let bestMatch: TapPattern | null = null;
      for (const e of newAlive) {
        if (e.depth >= e.pattern.taps) {
          if (!bestMatch || e.pattern.priority > bestMatch.priority) {
            bestMatch = e.pattern;
          }
        }
      }

      if (bestMatch) {
        // Pattern completed — resolve immediately.
        // Caught ring activates in invocation colour.
        ring.color = bestMatch.resolveColor ?? NEUTRAL_COLOR;
        ring.resolved = true;
        const mx = newTaps.reduce((s, t) => s + t.x, 0) / newTaps.length;
        const my = newTaps.reduce((s, t) => s + t.y, 0) / newTaps.length;
        this._spawnResolveBurst(mx, my, bestMatch.resolveColor ?? NEUTRAL_COLOR);
        this.onPatternResolved?.(bestMatch.id);
        return 'accepted';
      }

      // Continuation: deploy new ring schedule
      const tapIndex = newTaps.length - 1;
      const total = this._patterns.reduce((m, p) => Math.max(m, p.taps), 0);
      this._deploySchedule(newAlive, newTaps, tap.x, tap.y, now);
      this.onTapAccepted?.(tapIndex, total);
      return 'accepted';
    }

    // No ring caught the tap.  Spawn a fresh root ring unless an active
    // ring is nearby (within its spread radius) — that prevents spamming
    // duplicate roots on the same location while still allowing taps in
    // a completely different area to start their own chain.
    const SPAWN_DIST = 80; // px — must be far enough from existing rings
    const tooClose = this._rings.some(r => {
      if (r.phase === 'fading' || r.phase === 'dead') return false;
      const dx = tap.x - r.cx, dy = tap.y - r.cy;
      return Math.sqrt(dx * dx + dy * dy) < SPAWN_DIST;
    });
    if (!tooClose) {
      this._fadeAllRings(now);
      this._spawnRootRing(tap, now);
      const total = this._patterns.reduce((m, p) => Math.max(m, p.taps), 0);
      this.onTapAccepted?.(0, total);
    }
    return 'new-root';
  }

  // ── Rendering ──────────────────────────────────────────────────────────

  draw(ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number): void {
    const now = performance.now();

    // Tick all rings, handle timeouts + successor deployment
    for (let i = this._rings.length - 1; i >= 0; i--) {
      const ring = this._rings[i];
      const expired = ring.tick(now);
      if (expired) {
        // Ring expired — leave shadow + deploy successor
        this._shadows.push({
          cx: ring.cx, cy: ring.cy,
          color: ring.color,
          radius: ring.targetRadius,
          birthMs: now,
          durationMs: SHADOW_MS,
        });
        if (ring.successor) {
          this._deployFromSpec(ring.successor, ring.cx, ring.cy, now);
        }
      }
      if (ring.phase === 'dead') {
        this._rings.splice(i, 1);
      }
    }

    // ── Draw impact rings (first-tap feedback) ────────────────────────────
    for (let i = this._impacts.length - 1; i >= 0; i--) {
      const imp = this._impacts[i];
      const age = now - imp.birthMs;
      if (age > IMPACT_RING_MS) {
        this._impacts.splice(i, 1);
        continue;
      }
      const t = age / IMPACT_RING_MS;
      const r = IMPACT_RADIUS * easeOutCubic(t);
      const alpha = 0.12 * (1 - t);
      if (alpha < 0.002) continue;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = '#1a1610';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.arc(offsetX + imp.cx, offsetY + imp.cy, r, 0, TWO_PI);
      ctx.stroke();
      ctx.restore();
    }

    // ── Draw shadows ─────────────────────────────────────────────────────
    for (let i = this._shadows.length - 1; i >= 0; i--) {
      const sh = this._shadows[i];
      const age = now - sh.birthMs;
      if (age > sh.durationMs) {
        this._shadows.splice(i, 1);
        continue;
      }
      const alpha = 0.04 * (1 - age / sh.durationMs);
      if (alpha < 0.002) continue;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = '#3a3428';
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.arc(offsetX + sh.cx, offsetY + sh.cy, sh.radius, 0, TWO_PI);
      ctx.stroke();
      ctx.restore();
    }

    // ── Draw rings ───────────────────────────────────────────────────────
    for (const ring of this._rings) {
      this._drawRing(ctx, ring, now, offsetX, offsetY);
    }

    // ── Draw sparks (dust motes) ───────────────────────────────────────
    this._tickSparks();
    ctx.save();
    for (const s of this._sparks) {
      if (s.life <= 0) continue;
      ctx.globalAlpha = s.life * 0.2;
      ctx.fillStyle = '#3a3428';
      ctx.beginPath();
      ctx.arc(offsetX + s.x, offsetY + s.y, s.size * s.life, 0, TWO_PI);
      ctx.fill();
    }
    ctx.restore();
  }

  get active(): boolean {
    return this._rings.length > 0 || this._shadows.length > 0 || this._sparks.length > 0 || this._impacts.length > 0;
  }

  clear(): void {
    this._rings = [];
    this._shadows = [];
    this._sparks = [];
    this._impacts = [];
  }

  /**
   * Deploy a standalone one-shot terminal ring.
   * Used for continuation rings (e.g. analysis after wand invocation).
   * The ring resolves on pointer-down via checkPointerDown, or expires silently.
   */
  deployOneShot(
    cx: number, cy: number,
    color: string, windowMs: number, patternId: string,
    drawRadius = 16,
  ): void {
    const now = performance.now();
    const ring = new TapRing(
      cx, cy, now,
      color, windowMs,
      /* isTerminal */ true, patternId,
      /* alive */ [], /* taps */ [],
      /* successor */ null,
    );
    // One-shot hint rings: hit area tracks the visual ring tightly so
    // nearby drawing strokes are not accidentally intercepted.
    ring.targetRadius = drawRadius + 3;
    ring.drawRadius = drawRadius;
    ring.isOneShot = true;
    this._rings.push(ring);
  }


  // ── Internals — ring rendering ─────────────────────────────────────────

  private _drawRing(
    ctx: CanvasRenderingContext2D, ring: TapRing,
    now: number, ox: number, oy: number,
  ): void {
    const rx = ox + ring.cx;
    const ry = oy + ring.cy;
    const R = ring.drawRadius;

    if (ring.phase === 'expanding') {
      // Quick circle expansion
      const t = ring.expandT(now);
      const r = PROPAGATE_RADIUS * easeOutCubic(t);
      const alpha = 0.15 * t;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = GHOST_COLOR;
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      ctx.arc(rx, ry, r, 0, TWO_PI);
      ctx.stroke();
      ctx.restore();
      return;
    }

    if (ring.phase === 'active') {
      const t = ring.elapsed(now);
      // Sweeping arc: shrinks from full circle to nothing as time runs out
      const sweep = TWO_PI * (1 - t);
      const startAngle = ARC_START;
      const endAngle = ARC_START + sweep;

      const remaining = 1 - t;
      const urgency = remaining < 0.25 ? (1 + 0.08 * Math.sin(now * 0.012)) : 1;
      const baseAlpha = (0.55 + 0.25 * remaining) * urgency;

      ctx.save();

      // Continuous dark outline arc
      ctx.globalAlpha = baseAlpha;
      ctx.strokeStyle = ring.color;
      ctx.lineWidth = 1.2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.arc(rx, ry, R, startAngle, endAngle);
      ctx.stroke();

      ctx.restore();
      return;
    }

    if (ring.phase === 'fading') {
      const t = ring.fadeT(now);
      const scale = ring.resolved ? 1 + t * 0.3 : 1;
      const alpha = (ring.resolved ? 0.30 : 0.18) * (1 - t);
      if (alpha < 0.005) return;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = ring.color;
      ctx.lineWidth = 1.0;
      ctx.beginPath();
      ctx.arc(rx, ry, R * scale, 0, TWO_PI);
      ctx.stroke();
      ctx.restore();
    }
  }

  // ── Internals — ring spawning ──────────────────────────────────────────

  /** Spawn the initial root ring (first tap, no pattern context yet). */
  private _spawnRootRing(tap: RecordedTap, now: number): void {
    const taps = [tap];
    const alive: AliveEntry[] = this._patterns.map(p => ({ pattern: p, depth: 1 }));

    // Faint impact ring on the tap itself (visual feedback for first touch)
    this._impacts.push({ cx: tap.x, cy: tap.y, color: NEUTRAL_COLOR, birthMs: now });

    this._deploySchedule(alive, taps, tap.x, tap.y, now);
  }

  /** Build and deploy the ring schedule from alive entries. */
  private _deploySchedule(
    alive: AliveEntry[], taps: RecordedTap[],
    cx: number, cy: number, now: number,
  ): void {
    // Build the first ring's parameters by examining the alive+depth
    const specs = this._buildRingChain(alive, taps);
    if (!specs) return;
    this._deployFromSpec(specs, cx, cy, now);
  }

  /** Build a linked chain of RingSpecs from alive entries. */
  private _buildRingChain(
    alive: AliveEntry[], taps: RecordedTap[],
  ): RingSpec | null {
    if (alive.length === 0) return null;

    // For each alive entry, get the next rhythm gate
    interface GateEntry {
      entry: AliveEntry;
      gate: RhythmGate;
      isTerminal: boolean;
    }

    const gated: GateEntry[] = [];
    for (const e of alive) {
      const gapIdx = e.depth - 1; // 0-based gap index = depth - 1
      const gate = gateFor(e.pattern, gapIdx);
      const isTerminal = e.depth + 1 >= e.pattern.taps;
      gated.push({ entry: e, gate, isTerminal });
    }

    // Sort by gate start
    gated.sort((a, b) => a.gate.minMs - b.gate.minMs);

    // Build segments: contiguous time windows.
    // Use strict < so adjacent-but-non-overlapping windows stay separate
    // (e.g. THRICE_KNOCK [30,400] and STYLUS_SUMMON [400,1200]).
    interface Seg {
      startMs: number;
      endMs: number;
      entries: GateEntry[];
    }

    const segments: Seg[] = [];
    let curSeg: Seg = {
      startMs: gated[0].gate.minMs,
      endMs: gated[0].gate.maxMs,
      entries: [gated[0]],
    };

    for (let i = 1; i < gated.length; i++) {
      const g = gated[i];
      if (g.gate.minMs < curSeg.endMs) {
        // Genuinely overlapping — merge
        curSeg.endMs = Math.max(curSeg.endMs, g.gate.maxMs);
        curSeg.entries.push(g);
      } else {
        segments.push(curSeg);
        curSeg = { startMs: g.gate.minMs, endMs: g.gate.maxMs, entries: [g] };
      }
    }
    segments.push(curSeg);

    // Convert segments to RingSpecs.  Track cumulative deployment time so
    // each ring expires at the correct absolute offset from the original tap.
    type LinkedSpec = RingSpec & { _next?: LinkedSpec };
    const specs: LinkedSpec[] = [];
    let deployOffset = 0; // ms from original tap when this ring deploys

    for (const seg of segments) {
      let terminal = false;
      let resolveId: string | null = null;
      let bestPriority = -1;

      for (const g of seg.entries) {
        if (g.isTerminal && g.entry.pattern.priority > bestPriority) {
          terminal = true;
          resolveId = g.entry.pattern.id;
          bestPriority = g.entry.pattern.priority;
        }
      }

      // Pick colour: terminal rings get their pattern's resolve color,
      // non-terminal rings are neutral (whiteish).
      let color = NEUTRAL_COLOR;
      if (terminal) {
        const pat = seg.entries.find(
          g => g.isTerminal && g.entry.pattern.id === resolveId,
        )?.entry.pattern;
        color = pat?.resolveColor ?? NEUTRAL_COLOR;
      }

      // Window: ring should expire at seg.endMs from original tap.
      // It deploys at deployOffset and spends EXPAND_MS expanding first.
      const windowMs = Math.max(seg.endMs - deployOffset - EXPAND_MS, 50);

      specs.push({
        color,
        windowMs,
        isTerminal: terminal,
        resolvePatternId: resolveId,
        alive: seg.entries.map(g => g.entry),
        taps: [...taps],
      });

      deployOffset += EXPAND_MS + windowMs;
    }

    if (specs.length === 0) return null;

    // Wire successor chain
    for (let i = 0; i < specs.length - 1; i++) {
      specs[i]._next = specs[i + 1];
    }
    return specs[0];
  }

  /** Deploy a ring from a precomputed spec. */
  private _deployFromSpec(spec: RingSpec, cx: number, cy: number, now: number): void {
    const successor = (spec as RingSpec & { _next?: RingSpec })._next ?? null;
    const ring = new TapRing(
      cx, cy, now,
      spec.color, spec.windowMs,
      spec.isTerminal, spec.resolvePatternId,
      spec.alive, spec.taps,
      successor,
    );
    // Terminal rings need a generous hit area — users naturally spread
    // taps across a wider area than the visual ring radius.
    if (spec.isTerminal) {
      ring.targetRadius = 50;
    }
    this._rings.push(ring);
  }

  /** Resolve burst: subtle ripple + slow dust motes. */
  private _spawnResolveBurst(cx: number, cy: number, color: string): void {
    // Push a resolved ripple-like shadow (subdued)
    this._shadows.push({
      cx, cy, color,
      radius: TARGET_RADIUS * 2,
      birthMs: performance.now(),
      durationMs: 700,
    });

    // Dust motes — slow, dim, small particles drifting outward
    for (let i = 0; i < 10; i++) {
      const angle = Math.random() * TWO_PI;
      const speed = 0.3 + Math.random() * 0.8;
      this._sparks.push({
        x: cx, y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 0.15,
        life: 1,
        decay: 0.012 + Math.random() * 0.008,
        color,
        size: 1.0 + Math.random() * 1.0,
      });
    }
  }

  /** Fade all current rings to 'fading' phase. */
  private _fadeAllRings(now: number): void {
    for (const ring of this._rings) {
      if (ring.phase !== 'dead' && ring.phase !== 'fading') {
        // Leave shadow for active rings
        if (ring.phase === 'active') {
          this._shadows.push({
            cx: ring.cx, cy: ring.cy,
            color: ring.color,
            radius: ring.targetRadius,
            birthMs: now,
            durationMs: SHADOW_MS,
          });
        }
        ring.startFading(now);
      }
    }
  }

  /** Tick spark particles. */
  private _tickSparks(): void {
    for (const s of this._sparks) {
      s.x += s.vx;
      s.y += s.vy;
      s.vy += 0.03;
      s.life -= s.decay;
    }
    this._sparks = this._sparks.filter(s => s.life > 0);
  }
}
